import { useCallback, useContext, useEffect, useState } from 'react';
import TEXTS from '../controller/TextVariables';
import { InterfaceContext } from '../model/InterfaceProvider';
import { searchLookup, nominatimSearch, nominatimSearchStreet, photonSearch, nominatimLookup, mapboxSearch } from '../model/SearchLookup';
import { onemapSuggest, onemapFindAddress } from '../model/OnemapSearchLookup';
import ReactAutocomplete from 'react-autocomplete';
import { fromLonLat, toLonLat } from 'ol/proj';
import { featureProjection, debugMode } from '../model/StaticVariables';
import { Tooltip, StandardTooltipWindow } from './Tooltip';
import { transformExtent } from 'ol/proj';

const SearchUI = () => {
    const { setSearch, usState } = useContext(InterfaceContext);
    const usExtent = [-172.96875, 14.604847, -60.117188, 72.711903];
    const [extent, setExtent] = useState(usExtent);
    const [useMapBox, setUseMapBox] = useState(true);
    let controller;

    const [value, setvalue] = useState('');
    const [items, setItems] = useState([]);
    const [justScrolled, setJustScrolled] = useState(false);

    const convertBBoxToLonLat = (bounds) => {
        if (!bounds) return null;
        const coord1 = bounds.slice(0, 2);
        const coord2 = bounds.slice(2);
        const coord1LL = toLonLat(coord1, featureProjection);
        const coord2LL = toLonLat(coord2, featureProjection);
        return [...coord1LL, ...coord2LL];
    };

    const convertBBoxFromLonLat = useCallback((bounds) => {
        if (!bounds) return null;
        const center1 = [bounds[2] * 1, bounds[0] * 1];
        const center2 = [bounds[3] * 1, bounds[1] * 1];
        const center1CV = fromLonLat(center1, featureProjection);
        const center2CV = fromLonLat(center2, featureProjection);
        const extent = [...center1CV, ...center2CV];
        return extent;
    });
    const map_nominatim = useCallback((list) => {
        return list.map((item, i) => {
            return { id: i, label: item.display_name, extent: convertBBoxFromLonLat(item.boundingbox), bounds: item.boundingbox, value: [item.lat * 1, item.lon * 1] };
        });
    });
    const map_onemap = useCallback((list) => {
        if (!list) return [];
        let i = -1;
        return list.map((item) => {
            i++;
            return {
                id: i,
                origin: 'onemap',
                label: item.text,
                value: item.magicKey
            };
        });
    });
    const map_mapbox = useCallback((list, usState = false) => {
        if (!list) return [];
        return list.reduce((out, item, i) => {
            let bbox = item?.bbox ? item.bbox : [...item.center, ...item.center];
            let newBBox = [bbox[1], bbox[3], bbox[0], bbox[2]];
            if (usState) {
                let context = item.context.find((ctx) => ctx.id.substring(0, 6) === 'region');
                if (context.text !== usState.label) return out;
            }
            let newObj = {
                id: i + 1,
                origin: 'mapbox',
                placeID: item.id,
                type: item.place_type,
                label: item.place_name,
                extent: convertBBoxFromLonLat(newBBox),
                bounds: newBBox,
                value: item.center
            };
            out.push(newObj);
            return out;
        }, []);
    });
    const map_photon = useCallback((list) => {
        if (!list) return [];
        return list
            .filter((place) => {
                return place.properties.countrycode === 'US';
            })
            .map((item, i) => {
                const { city = '', postcode = '', housenumber = '', street = '', name = '', extent = false, osm_key = '', state = '', osm_id: id, osm_type: type } = item.properties;
                let address1 = name + [housenumber || '', street || ''].join(' ') || '';
                let cityState = [city, state].filter((e) => e).join(', ');
                let label = [address1, cityState, postcode || '']
                    .filter((i) => i)
                    .join(', ')
                    .replace(' ,', ',');
                let newext = extent ? [extent[3], extent[1], extent[0], extent[2]] : [];
                return {
                    id: i + 1,
                    origin: 'photo',
                    placeID: id,
                    type,
                    label,
                    bounds: convertBBoxFromLonLat(newext),
                    value: item.geometry.coordinates
                };
            });
    });
    const couldBeZipCode = (val) => {
        val = val.trim();
        if (val.length > 5) return false;
        if (isNaN(val)) return false;
        return true;
    };
    const couldBeHouse = (val) => {
        const regex = /[a-zA-Z]/g;
        val = val.trim();
        let firstLet = val.substring(0, 1);
        if (isNaN(firstLet) || val.length < 5) return false; //5 is arbitrary number here
        if (val.search(regex) < 1) return false; //must have a letter not at the beginning
        return true;
    };

    const fallbackSearch = async (value, extent, signal) => {
        photonSearch(value, extent, controller.signal)
            // searchLookup(value, extent, controller.signal)
            .then(async (list) => {
                if (!list) return;
                var lookups = map_photon(list.features);
                const zipItem = couldBeZipCode(value) ? await nominatimSearch(value) : false;
                const houseItem = couldBeHouse(value) ? await nominatimSearchStreet(value) : false;
                if (zipItem) {
                    let mzip = map_nominatim(zipItem);
                    lookups = [mzip[0], ...lookups];
                }
                if (houseItem?.length) {
                    let mhouse = map_nominatim(houseItem);
                    lookups = [mhouse[0], ...lookups];
                }
                setItems(lookups);
            })
            .catch((err) => {
                console.log(err);
                console.log('error on lookup');
                setItems([]);
            });
    };
    const getSelectionBBox = async (obj) => {
        //Onemap
        if (obj.origin === 'onemap') {
            const onemapBoundingBox = await onemapFindAddress(obj.value, extent).then((res) => {
                const foundItem = res.data.candidates[0].extent;
                return [foundItem.xmin, foundItem.ymin, foundItem.xmax, foundItem.ymax];
            });
            const converted = transformExtent(onemapBoundingBox, 'EPSG:4326', 'EPSG:3857');
            return converted;
        }
        //mapbox
        if (obj.extent) return obj.extent;
        //photon/nominatim
        const precise = await nominatimLookup(obj.placeID, obj.type);
        return convertBBoxFromLonLat(precise.boundingbox);
    };
    const onInputEnter = (e) => {
        if (e.key == 'Enter') {
            getSelectionBBox(items[0]).then((preExtent) => setSearch(preExtent));
            setvalue('');
            e.target.blur();
        }
    };
    useEffect(() => {
        window.addEventListener('scroll', () => {
            //close the menu and remove focus from dropdown
            setJustScrolled(true);
            document.activeElement.blur();
        });
    }, []);

    useEffect(() => {
        setExtent(convertBBoxToLonLat(usState?.bounds) || usExtent);
    }, [usState]);
    useEffect(() => {
        if (!value) {
            setItems([]);
            return;
        }

        if (controller) {
            controller.abort();
        }
        controller = new AbortController();

        var useOneMap = debugMode; //!!! When ready to switch back to using onemapsuggest change to true
        if (useOneMap) {
            console.log('use onemap for search');
            onemapSuggest(value, extent.join(',')).then((res) => {
                const mappedItems = map_onemap(res.data.suggestions);
                setItems(mappedItems);
            });
        } else if (useMapBox) {
            mapboxSearch(value, extent, controller.signal)
                .then((list) => {
                    var lookups = map_mapbox(list.features, usState);
                    setItems(lookups);
                })
                .catch((err) => {
                    console.log('>>> err', err);
                    setUseMapBox(false);
                    fallbackSearch(value, extent, controller.signal);
                });
        } else {
            fallbackSearch(value, extent, controller.signal);
        }
    }, [value]);
    return (
        <>
            <div className="field " id="search">
                <div className="flexwrap ">
                    <label>Search within: {usState?.label || 'U.S.'}</label>
                    <span className="icon-search"></span>
                    <ReactAutocomplete
                        items={items}
                        inputProps={{ placeholder: TEXTS.controls.search_box.label }}
                        getItemValue={(item) => item.label}
                        renderItem={(item, highlighted) =>
                            item && (
                                <div key={item.id} className={'auto-item' + (highlighted ? ' highlight' : '')}>
                                    {item.label}
                                </div>
                            )
                        }
                        value={value}
                        open={!justScrolled && items.length > 0}
                        renderInput={(props) => {
                            return <input aria-label={TEXTS.controls.search_box.label} {...props} onKeyDown={onInputEnter} />;
                        }}
                        onChange={async (e) => {
                            if (controller) {
                                controller.abort();
                            }
                            setvalue(e.target.value);
                            return e.target.value;
                        }}
                        onSelect={async (value, obj) => {
                            const preExtent = await getSelectionBBox(obj);
                            setvalue('');
                            setSearch(preExtent);
                            return value;
                        }}
                        onMenuVisibilityChange={(e) => {
                            //Reset scroll tracker when user clicks inside the input
                            setJustScrolled(false);
                        }}
                    />
                    <Tooltip id={'search-tip'} tooltip={TEXTS.controls.search_box.tooltip} window={<StandardTooltipWindow id={'search-tip'} text={TEXTS.controls.search_box.tooltip} />}>
                        <div className="reset icon-info"></div>
                    </Tooltip>
                </div>
            </div>
        </>
    );
};

export default SearchUI;
